import React, { useState, useEffect } from "react";
import {
  Document, Page, Image, Text, View, StyleSheet, Font,
  Svg, Path, Circle, Rect, Defs, LinearGradient, Stop,
} from '@react-pdf/renderer';
import CircularProgress from '@mui/material/CircularProgress';
import { withRouter } from "../../providers/withRouter";
import PDFHandler from "./PDFHandler";
import RobotoRegular from "../../fonts/Poppins-Regular.ttf";
import RobotoBold from "../../fonts/Poppins-Bold.ttf";
import RobotoItalic from "../../fonts/Poppins-Italic.ttf";
import RobotoBoldItalic from "../../fonts/Poppins-BoldItalic.ttf";
import logoIntegracces from "../../images/logo integracces.png";
import firmaArquitecto from "../../images/firma arquitecto.png";
import sealStamp from "../../images/stamp-accesibilidad.png";
import { getVisitSheetCai, getVisitSheetIdFromSale } from "../../services";

Font.register({
  family: 'Poppins',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 'bold' },
    { src: RobotoItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});
Font.register({ family: 'Poppins-Bold', src: RobotoBold });

Font.register({
  family: 'Inter',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 'bold' },
    { src: RobotoItalic, fontWeight: 'normal', fontStyle: 'italic' },
    { src: RobotoBoldItalic, fontWeight: 'bold', fontStyle: 'italic' },
  ],
});
Font.register({ family: 'Inter-Bold', src: RobotoBold });
Font.register({ family: 'Inter-ExtraBold', src: RobotoBold });

Font.register({
  family: 'JetBrainsMono',
  fonts: [
    { src: RobotoRegular, fontWeight: 'normal' },
    { src: RobotoBold, fontWeight: 600 },
    { src: RobotoBold, fontWeight: 'bold' },
  ],
});
Font.register({ family: 'JetBrainsMono-Bold', src: RobotoBold });
Font.register({ family: 'JetBrainsMono-SemiBold', src: RobotoBold });

Font.registerHyphenationCallback((word) => [word]);

const F = {
  sans: 'Inter',
  sansBold: 'Inter-Bold',
  sansExtraBold: 'Inter-ExtraBold',
  mono: 'JetBrainsMono',
  monoBold: 'JetBrainsMono-Bold',
  monoSemiBold: 'JetBrainsMono-SemiBold',
};

const C = {
  blue: '#1F7EBF',
  blueDeep: '#155A8C',
  blueDarker: '#0E456E',
  green: '#8FC93F',
  greenDeep: '#6BA02A',
  greenDarker: '#4F7B1F',
  red: '#D14843',
  redDeep: '#A8332E',
  amber: '#C8851F',
  amberFg: '#8A5A00',
  ink: '#0F1620',
  ink2: '#2A3340',
  muted: '#6E6E6E',
  line: '#E4E7EC',
  lineStrong: '#C9CFD7',
  surface: '#FFFFFF',
  surface2: '#F8F9FA',
  surface3: '#F1F4F7',
  tintBlue: '#EAF3FB',
  tintBlueSection: '#F0F7FC',
  tintBlueLine: '#C9DFEF',
  tintGreen: '#F0F8E2',
  tintGreenSection: '#F4F9E8',
  tintGreenLine: '#D2E8AC',
  tintRed: '#FCEDEC',
  tintRedLine: '#F1C9C7',
  amberBg: '#FFF6E5',
  amberLine: '#F0D89A',
};

const gradeBucket = (pct) => {
  const n = parseInt(pct, 10);
  if (isNaN(n)) return 'partial';
  if (n > 70) return 'pass';
  if (n < 50) return 'fail';
  return 'partial';
};

const heroState = (pct) => {
  const n = parseInt(pct, 10);
  if (isNaN(n)) return { key: 'parcial', label: 'Parcialmente accesible' };
  if (n > 70) return { key: 'accesible', label: 'Accesible' };
  if (n >= 50) return { key: 'parcial', label: 'Parcialmente accesible' };
  return { key: 'no-accesible', label: 'No accesible' };
};

const isNoApplicable = (dim) => {
  const v = (dim?.resp ?? dim?.respuesta ?? '').toString().trim().toLowerCase();
  return v === 'np';
};

const isAbsent = (dim) => {
  const v = (dim?.resp ?? dim?.respuesta ?? '').toString().trim().toLowerCase();
  return v === 'no';
};

const tileState = (dim) => {
  if (isNoApplicable(dim)) return 'na';
  const b = gradeBucket(dim.porcentaje_categoria);
  if (b === 'pass') return 'cumple';
  if (b === 'partial') return 'parcial';
  return 'critico';
};

const TILE_TOKENS = {
  cumple: {
    bg: C.tintGreen, border: C.tintGreenLine,
    nameColor: C.greenDarker, gradeColor: C.greenDarker, iconColor: C.greenDarker,
    barFill: C.greenDeep, stateColor: C.greenDarker, stateBorder: C.tintGreenLine,
    label: 'Cumple',
  },
  parcial: {
    bg: C.amberBg, border: C.amberLine,
    nameColor: C.amberFg, gradeColor: C.amberFg, iconColor: C.amberFg,
    barFill: C.amber, stateColor: C.amberFg, stateBorder: C.amberLine,
    label: 'Parcial',
  },
  critico: {
    bg: C.tintRed, border: C.tintRedLine,
    nameColor: C.redDeep, gradeColor: C.redDeep, iconColor: C.redDeep,
    barFill: C.red, stateColor: C.redDeep, stateBorder: C.tintRedLine,
    label: 'Critico',
  },
  na: {
    bg: C.tintGreen, border: C.tintGreenLine,
    nameColor: C.greenDarker, gradeColor: C.greenDarker, iconColor: C.greenDarker,
    barFill: C.greenDeep, stateColor: C.greenDarker, stateBorder: C.tintGreenLine,
    label: 'No aplicable',
  },
};

const synthesizeTileDescription = (dim, naMap) => {
  if (isNoApplicable(dim)) {
    return (naMap && naMap[dim.nombre_categoria]) || 'No aplicable';
  }
  const subs = Object.values(dim.subquestions || {});
  const total = subs.length;
  if (total === 0) return '';
  if (isAbsent(dim)) {
    return `${total} parámetros · 0 cumple · ${total} no cumple`;
  }
  const cumple = subs.filter((s) => s?.cumplimiento === 'cumple').length;
  const noCumple = subs.filter((s) => s?.cumplimiento === 'no cumple').length;
  return `${total} parámetros · ${cumple} cumple · ${noCumple} no cumple`;
};

const categoryOrderIndex = (name) => {
  const n = (name || '').toLowerCase().trim();
  if (n.startsWith('itinerario')) return 0;
  if (n.startsWith('acceso')) return 1;
  if (n.startsWith('desnivel')) return 2;
  if (n.startsWith('rampa')) return 3;
  if (n.startsWith('ascensor')) return 4;
  if ((n.includes('plataforma') || n.includes('plataformas')) && n.includes('vertical')) return 5;
  if ((n.includes('plataforma') || n.includes('plataformas')) && n.includes('inclinad')) return 6;
  if (n.startsWith('aparcamiento')) return 7;
  return 999;
};

const sortDimsByCategory = (dims) =>
  [...dims].sort((a, b) => categoryOrderIndex(a.nombre_categoria) - categoryOrderIndex(b.nombre_categoria));

const normalizeAnswersCai = (raw) => {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  if (list.length === 0) return [];

  const alreadyNested = list.some((item) => {
    const subs = item?.subquestions;
    if (Array.isArray(subs)) return subs.length > 0;
    if (subs && typeof subs === 'object') return Object.keys(subs).length > 0;
    return false;
  });
  if (alreadyNested) return list;

  const sorted = [...list].sort((a, b) => {
    const ai = Number(a?.id);
    const bi = Number(b?.id);
    if (Number.isNaN(ai) || Number.isNaN(bi)) return 0;
    return ai - bi;
  });

  const groups = [];
  let current = null;
  for (const row of sorted) {
    if (!row) continue;
    const hasCategory = row.nombre_categoria && String(row.nombre_categoria).trim() !== '';
    if (hasCategory) {
      current = { ...row, subquestions: [] };
      groups.push(current);
    } else if (current) {
      current.subquestions.push(row);
    }
  }
  return groups;
};

const styles = StyleSheet.create({
  contentPage: {
    paddingTop: '12mm',
    paddingBottom: '14mm',
    paddingHorizontal: '14mm',
    backgroundColor: C.surface,
    fontFamily: F.sans,
    fontSize: 10,
    lineHeight: 1.15,
    color: C.ink,
  },
  pageFooter: {
    position: 'absolute',
    bottom: '8mm',
    left: '14mm',
    right: '14mm',
    fontSize: 8,
    color: C.blueDeep,
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  pageFooterLeft: { fontFamily: F.sans, fontSize: 8, color: C.blueDeep },

  cover: {
    borderWidth: 1,
    borderColor: C.line,
    borderTopWidth: 4,
    borderTopColor: C.blue,
    borderRadius: 6,
    overflow: 'visible',
    position: 'relative',
    marginBottom: 8,
  },
  coverTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 11,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: C.tintBlueLine,
    position: 'relative',
  },
  coverTopGradientWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  coverBrand: { flexDirection: 'row', alignItems: 'center' },
  coverBrandImg: { height: 30 },
  coverMeta: { flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  tag: {
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    letterSpacing: 0.4,
    color: C.surface,
    backgroundColor: C.blue,
    paddingVertical: 3,
    paddingHorizontal: 9,
    borderRadius: 999,
    textTransform: 'uppercase',
  },
  coverDocId: { fontFamily: F.mono, fontSize: 8.5, color: C.blueDeep },

  coverTitleRow: { paddingTop: 13, paddingHorizontal: 14, paddingBottom: 12 },
  coverEyebrow: {
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    letterSpacing: 0.7,
    color: C.blueDeep,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  coverTitle: {
    fontSize: 22,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    lineHeight: 1.08,
    marginBottom: 3,
  },
  coverSubtitle: { fontFamily: F.sans, fontSize: 10, color: C.ink2 },

  stamp: {
    position: 'absolute',
    top: '28mm',
    right: '16mm',
    width: '44mm',
    height: '44mm',
    opacity: 0.32,
  },

  hero: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 14,
    paddingTop: 14,
    paddingBottom: 24,
    paddingHorizontal: 28,
    borderWidth: 1,
    borderColor: C.tintGreenLine,
    borderRadius: 8,
    marginBottom: 16,
    backgroundColor: C.tintGreenSection,
  },
  heroLeft: { flexDirection: 'column', flexGrow: 1, flexShrink: 1, gap: 4, alignSelf: 'center' },
  heroEyebrow: {
    fontFamily: F.monoSemiBold,
    fontSize: 8,
    letterSpacing: 0.9,
    color: C.blueDeep,
    textTransform: 'uppercase',
  },
  heroComunidad: {
    fontSize: 14,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    lineHeight: 1.2,
  },
  heroLoc: { fontSize: 9.5, fontFamily: F.mono, color: C.ink2, marginTop: 2 },
  heroDate: { fontSize: 9, fontFamily: F.mono, color: C.muted, marginTop: 4, letterSpacing: 0.2 },

  heroRight: { flexDirection: 'column', alignItems: 'flex-end', alignSelf: 'flex-start' },
  heroScoreValue: { flexDirection: 'row', alignItems: 'baseline', marginBottom: 28 },
  heroScoreNum: {
    fontFamily: F.sansBold,
    fontSize: 62,
    lineHeight: 0.78,
    color: C.greenDeep,
    marginRight: 4,
  },
  heroScorePct: {
    fontFamily: F.sansBold,
    fontSize: 26,
    lineHeight: 0.78,
    color: C.green,
    marginLeft: 2,
  },
  heroBadge: {
    paddingVertical: 4,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
  },
  heroBadgeText: {
    fontFamily: F.monoBold,
    fontSize: 9.5,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: C.surface,
    textAlign: 'center',
  },

  section: { marginTop: 8, marginBottom: 8 },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderBottomWidth: 2,
    borderBottomColor: C.blue,
    paddingBottom: 5,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    flexGrow: 1,
  },

  dimGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  dimTile: {
    width: '49%',
    borderWidth: 1,
    borderColor: C.line,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 11,
    backgroundColor: C.surface,
  },
  dimTileTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 9,
    marginBottom: 4,
  },
  dimIconWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    height: 14,
  },
  dimName: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 4,
    fontSize: 10,
    fontFamily: F.sansBold,
    color: C.blueDeep,
    lineHeight: 1.15,
  },
  dimGradeNum: {
    fontFamily: F.sansExtraBold,
    fontSize: 13,
    color: C.blueDeep,
  },
  dimGradePct: {
    fontFamily: F.sansBold,
    fontSize: 9,
    color: C.muted,
    marginLeft: 1,
  },
  dimBar: {
    height: 4,
    backgroundColor: C.surface3,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 4,
    flexDirection: 'row',
  },
  dimBarFill: {
    height: 4,
    backgroundColor: C.blue,
    borderRadius: 2,
  },
  dimBottom: {
    marginTop: 'auto',
    flexDirection: 'column',
  },
  dimMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  dimDesc: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 4,
    fontFamily: F.mono,
    fontSize: 7.4,
    color: C.ink2,
    lineHeight: 1.3,
  },
  dimStateChip: {
    paddingVertical: 1.5,
    paddingHorizontal: 6,
    borderRadius: 999,
    borderWidth: 0.5,
    backgroundColor: C.surface,
    alignSelf: 'flex-start',
  },
  dimStateChipText: {
    fontFamily: F.monoBold,
    fontSize: 7.2,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },

  signoff: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 2,
    borderTopColor: C.blue,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-end',
  },
  signoffLeft: { flexShrink: 1, flexGrow: 1, flexBasis: 0, paddingRight: 16 },
  signoffNote: {
    fontFamily: F.sans,
    fontSize: 9.0,
    color: C.ink2,
    lineHeight: 1.35,
  },
  signoffDate: {
    fontFamily: F.mono,
    fontSize: 8.5,
    color: C.blueDeep,
    marginTop: 6,
    letterSpacing: 0.2,
  },
  signature: {
    width: 220,
    borderWidth: 1,
    borderColor: C.tintGreenLine,
    borderTopWidth: 3,
    borderTopColor: C.greenDeep,
    borderRadius: 6,
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 12,
    backgroundColor: C.surface,
    alignItems: 'center',
  },
  signatureImg: { width: 30, marginBottom: 5 },
  signatureName: { fontFamily: F.sansBold, fontSize: 9},
  signatureSub: { fontFamily: F.mono, fontSize: 9, color: C.ink2, marginTop: 1 },
  signatureRole: { fontSize: 8.5 },
});

const dimStrokeProps = (color) => ({
  fill: 'none',
  stroke: color,
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

const DIM_ICONS = {
  itinerario: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Circle cx="4" cy="20" r="1.3" fill={color} />
      <Path d="M4 20Q18 16 12 12Q6 8 15 6" {...dimStrokeProps(color)} />
      <Path d="M12 5L15 6L13 8" {...dimStrokeProps(color)} />
      <Path d="M17 3L21 7M21 3L17 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  acceso: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Path d="M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5" {...dimStrokeProps(color)} />
      <Path d="M3 12h12" {...dimStrokeProps(color)} />
      <Path d="M11 8L15 12L11 16" {...dimStrokeProps(color)} />
    </Svg>
  ),
  desnivel: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Path d="M3 19H8V14H14V9H21" {...dimStrokeProps(color)} />
    </Svg>
  ),
  rampa: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Path d="M3 19H21V7Z" {...dimStrokeProps(color)} />
      <Path d="M6 10L15 4" {...dimStrokeProps(color)} />
      <Path d="M12 4L15 4L14 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  ascensor: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Rect x="4" y="3" width="16" height="18" rx="1.5" {...dimStrokeProps(color)} />
      <Path d="M12 4v16" {...dimStrokeProps(color)} />
      <Path d="M8 10L9.5 8L11 10" {...dimStrokeProps(color)} />
      <Path d="M9.5 8v4" {...dimStrokeProps(color)} />
      <Path d="M13 14L14.5 16L16 14" {...dimStrokeProps(color)} />
      <Path d="M14.5 12v4" {...dimStrokeProps(color)} />
    </Svg>
  ),
  'plataformas-verticales': (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Path d="M5 4v16M19 4v16" {...dimStrokeProps(color)} />
      <Rect x="6" y="14" width="12" height="3" rx="0.5" {...dimStrokeProps(color)} />
      <Path d="M9 11V6" {...dimStrokeProps(color)} />
      <Path d="M9 6L7.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M9 6L10.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M15 11V6" {...dimStrokeProps(color)} />
      <Path d="M15 6L13.5 7.5" {...dimStrokeProps(color)} />
      <Path d="M15 6L16.5 7.5" {...dimStrokeProps(color)} />
    </Svg>
  ),
  'plataformas-inclinadas': (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Path d="M3 19L9 14" {...dimStrokeProps(color)} />
      <Path d="M13 11L21 4" {...dimStrokeProps(color)} />
      <Rect x="6" y="11" width="12" height="3" rx="0.4" {...dimStrokeProps(color)} />
      <Path d="M3 10L11 4" {...dimStrokeProps(color)} />
      <Path d="M8 4L11 4L10 7" {...dimStrokeProps(color)} />
    </Svg>
  ),
  aparcamiento: (color) => (
    <Svg viewBox="0 0 24 24" width={14} height={14}>
      <Rect x="4" y="4" width="16" height="16" rx="2.5" {...dimStrokeProps(color)} />
      <Path d="M10 17V7h3.2a2.8 2.8 0 0 1 0 5.6H10" {...dimStrokeProps(color)} />
    </Svg>
  ),
};

const resolveDimIconKey = (name) => {
  const n = (name || '').toLowerCase().trim();
  if (n.startsWith('itinerario')) return 'itinerario';
  if (n.startsWith('acceso')) return 'acceso';
  if (n.startsWith('desnivel')) return 'desnivel';
  if (n.startsWith('rampa')) return 'rampa';
  if (n.startsWith('ascensor')) return 'ascensor';
  if (n.includes('plataformas') && n.includes('vertical')) return 'plataformas-verticales';
  if (n.includes('plataformas') && n.includes('inclinad')) return 'plataformas-inclinadas';
  if (n.startsWith('aparcamiento')) return 'aparcamiento';
  return null;
};

const DimIcon = ({ name, color }) => {
  const key = resolveDimIconKey(name);
  if (!key || !DIM_ICONS[key]) return null;
  return DIM_ICONS[key](color);
};

const InformeAccesibilidadCAIPDF = (props) => {
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const sale = await getVisitSheetIdFromSale(props.params.id);
    const visitSheetCai_id = sale.detailsCai[0]?.visitSheetCai_id;
    const response = await getVisitSheetCai(visitSheetCai_id);
    setFormData(response.data);
    setIsLoading(false);
  };

  const data = formData || {};
  const comunidadName = (data.propertie && data.propertie.razonSocial) || '';
  const direccion = (data.propertie && data.propertie.direccion) || data.direccion || '';
  const fechaVisita = data.visitDate || data.fechaVisita || '';
  const lugarFirma = data.propertie?.municipio || '';
  const diaFirma = new Date(data.visitDate).getDate() || '';
  const mesFirma = new Date(data.visitDate).toLocaleString('es-ES', { month: 'long' }) || '';
  const anoFirma = new Date(data.visitDate).getFullYear() || '';
  const porcentaje_total = data.porcentaje_total || '';
  const docId = data.docId || '';
  const answersCai = data.answersCai || [];
  const naDescriptions = data.naDescriptions || {};

  const fechaVisitaStr = fechaVisita ? new Date(fechaVisita).toLocaleDateString('es-ES') : '';
  const totalNum = parseInt(porcentaje_total, 10);
  const stampVisible = !isNaN(totalNum) && totalNum >= 80;
  const hero = heroState(porcentaje_total);

  const heroScoreColor = hero.key === 'accesible' ? C.greenDarker
    : hero.key === 'parcial' ? C.amberFg
    : C.redDeep;
  const heroScorePctColor = hero.key === 'accesible' ? C.greenDeep
    : hero.key === 'parcial' ? C.amber
    : C.red;
  const heroBadgeBg = hero.key === 'accesible' ? C.greenDeep
    : hero.key === 'parcial' ? C.amber
    : C.red;
  const heroBadgeBorder = hero.key === 'accesible' ? C.greenDarker
    : hero.key === 'parcial' ? C.amberFg
    : C.redDeep;

  const dims = sortDimsByCategory(normalizeAnswersCai(answersCai));

  return (
    <>
      {isLoading ? (
        <CircularProgress style={{ color: '#92004D', position: 'absolute', top: '50%', left: '50%' }} />
      ) : (
        <PDFHandler>
          <Document>
            <Page size="A4" style={styles.contentPage}>

              <View style={styles.cover} wrap={false}>
                <View style={styles.coverTop}>
                  <Svg style={styles.coverTopGradientWrap}>
                    <Defs>
                      <LinearGradient id="coverTopGrad" x1="0" y1="0" x2="0" y2="1">
                        <Stop offset="0" stopColor={C.tintBlueLine} />
                        <Stop offset="1" stopColor={C.surface} />
                      </LinearGradient>
                    </Defs>
                    <Rect x="0" y="0" width="100%" height="100%" fill="url(#coverTopGrad)" />
                  </Svg>
                  <View style={styles.coverBrand}>
                    <Image src={logoIntegracces} style={styles.coverBrandImg} />
                  </View>
                  <View style={styles.coverMeta}>
                    <Text style={styles.tag}>Informe · Accesibilidad</Text>
                    {!!docId && <Text style={styles.coverDocId}>{docId}</Text>}
                  </View>
                </View>
                <View style={styles.coverTitleRow}>
                  <Text style={styles.coverEyebrow}>DECRET 209/2023 · Codi d'accessibilitat de Catalunya</Text>
                  <Text style={styles.coverTitle}>Informe de Accesibilidad</Text>
                  <Text style={styles.coverSubtitle}>Informe Indicativo del Grado de Accesibilidad del Edificio</Text>
                </View>
              </View>

              <View style={styles.hero} wrap={false}>
                <View style={styles.heroLeft}>
                  <Text style={styles.heroEyebrow}>Grado de accesibilidad</Text>
                  <Text style={styles.heroComunidad}>{comunidadName || '__'}</Text>
                  <Text style={styles.heroLoc}>{direccion || '__'}</Text>
                  <Text style={styles.heroDate}>Fecha de inspección · {fechaVisitaStr || '__'}</Text>
                </View>
                <View style={styles.heroRight}>
                  <View style={styles.heroScoreValue}>
                    <Text style={[styles.heroScoreNum, { color: heroScoreColor }]}>
                      {isNaN(totalNum) ? '__' : totalNum}
                    </Text>
                    <Text style={[styles.heroScorePct, { color: heroScorePctColor }]}>%</Text>
                  </View>
                  <View style={[styles.heroBadge, { backgroundColor: heroBadgeBg, borderColor: heroBadgeBorder }]}>
                    <Text style={styles.heroBadgeText}>{hero.label}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.section}>
                <View style={styles.sectionHead}>
                  <Text style={styles.sectionTitle}>Resumen por dimensión</Text>
                </View>
                <View style={styles.dimGrid}>
                  {dims.map((dim, di) => {
                    const state = tileState(dim);
                    const tok = TILE_TOKENS[state];
                    const pct = parseInt(dim.porcentaje_categoria, 10);
                    const fillFlex = isNaN(pct) ? 0 : (pct < 0 ? 0 : (pct > 100 ? 100 : pct));
                    const desc = synthesizeTileDescription(dim, naDescriptions);
                    return (
                      <View
                        key={di}
                        style={[styles.dimTile, { backgroundColor: tok.bg, borderColor: tok.border }]}
                        wrap={false}
                      >
                        <View style={styles.dimTileTopRow}>
                          <View style={styles.dimIconWrap}>
                            <DimIcon name={dim.nombre_categoria} color={tok.iconColor} />
                          </View>
                          <Text style={[styles.dimName, { color: tok.nameColor }]}>
                            {dim.nombre_categoria}
                          </Text>
                          <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                            <Text style={[styles.dimGradeNum, { color: tok.gradeColor }]}>
                              {isNaN(pct) ? '__' : pct}
                            </Text>
                            <Text style={[styles.dimGradePct, { color: tok.gradeColor, opacity: 0.75 }]}>%</Text>
                          </View>
                        </View>
                        <View style={styles.dimBottom}>
                          <View style={styles.dimBar}>
                            <View style={[styles.dimBarFill, { flex: fillFlex, backgroundColor: tok.barFill }]} />
                            <View style={{ flex: 100 - fillFlex }} />
                          </View>
                          <View style={styles.dimMeta}>
                            <Text style={styles.dimDesc}>{desc}</Text>
                            <View style={[styles.dimStateChip, { borderColor: tok.stateBorder }]}>
                              <Text style={[styles.dimStateChipText, { color: tok.stateColor }]}>
                                {tok.label}
                              </Text>
                            </View>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>

              <View style={styles.signoff} wrap={false}>
                <View style={styles.signoffLeft}>
                  <Text style={styles.signoffNote}>
                    Los detalles técnicos y la enumeración completa de parámetros se recogen en el
                    Certificado de Accesibilidad adjunto, que acompaña a este informe.
                  </Text>
                  <Text style={styles.signoffDate}>
                    En {lugarFirma || '__'}, a {diaFirma || '__'} de {mesFirma || '__'} de {anoFirma ? (String(anoFirma).startsWith('20') ? anoFirma : `20${anoFirma}`) : '20__'}.
                  </Text>
                </View>
                <View style={styles.signature}>
                  <Image src={firmaArquitecto} style={styles.signatureImg} />
                  <Text style={styles.signatureName}>Angulo Arquitectura, S.L.P.</Text>
                  <Text style={styles.signatureSub}>F. Borja Angulo Angulo</Text>
                  <Text style={styles.signatureRole}>Arquitecto</Text>
                </View>
              </View>

              {stampVisible && <Image src={sealStamp} style={styles.stamp} />}

              <View style={styles.pageFooter} fixed>
                <Text style={styles.pageFooterLeft}>Informe de Accesibilidad · Integracces</Text>
              </View>
            </Page>
          </Document>
        </PDFHandler>
      )}
    </>
  );
};

export default withRouter(InformeAccesibilidadCAIPDF);
